import type { RenderCamera, RenderDecoration, RenderFilter, RenderPoint, RenderRect, RenderSprite, RenderTerrainChunk } from './rendererAdapter.js';

export const screenScale = (camera: RenderCamera): number => camera.state.zoom * camera.dpr;
export const samplingFilter = (camera: RenderCamera, level: number): RenderFilter => Math.abs(screenScale(camera) / level - Math.round(screenScale(camera) / level)) <= 1e-6 ? 'nearest' : 'linear';
export const cameraTranslation = (camera: RenderCamera): RenderPoint => {
  const s=screenScale(camera), [x,y]=camera.state.center, q=camera.state.rotation;
  let rx=x, ry=y; for(let i=0;i<q;i+=1)[rx,ry]=[-ry,rx];
  return [Math.round(camera.viewportCss[0]*camera.dpr)/2-s*(32*(rx-ry)), Math.round(camera.viewportCss[1]*camera.dpr)/2-s*(16*(rx+ry))];
};
export const spriteGeometry=(sprite:RenderSprite,camera:RenderCamera):{anchor:RenderPoint;rect:RenderRect;clip:{pass:'back'|'front';rect:RenderRect}|null;filter:RenderFilter}=>{
  const s=screenScale(camera), a=s/sprite.level, c=cameraTranslation(camera), anchor:[number,number]=[sprite.unitAnchor[0]*s+c[0],sprite.unitAnchor[1]*s+c[1]], integer=Math.abs(a-Math.round(a))<=1e-6;
  const x=(integer?Math.round(anchor[0]-sprite.frameAnchor[0]*a):anchor[0]-sprite.frameAnchor[0]*a), y=(integer?Math.round(anchor[1]-sprite.frameAnchor[1]*a):anchor[1]-sprite.frameAnchor[1]*a), rect:[number,number,number,number]=[x,y,sprite.frameRect[2]*a,sprite.frameRect[3]*a];
  const split=integer?Math.round(anchor[1]):anchor[1], clip=sprite.clip?{pass:sprite.clip,rect:[x,sprite.clip==='back'?y:split,rect[2],Math.max(0,sprite.clip==='back'?split-y:y+rect[3]-split)] as RenderRect}:null;
  return {anchor,rect,clip,filter:samplingFilter(camera,sprite.level)};
};
export const decorationRect=(d:RenderDecoration,camera:RenderCamera,level:number):{anchor:RenderPoint;rect:RenderRect;filter:RenderFilter}=>{const s=screenScale(camera),c=cameraTranslation(camera),a:[number,number]=[d.unitAnchor[0]*s+c[0],d.unitAnchor[1]*s+c[1]],w=d.unitSize[0]*s,h=d.unitSize[1]*s,cos=Math.abs(Math.cos(d.angle)),sin=Math.abs(Math.sin(d.angle)),rw=w*cos+h*sin,rh=w*sin+h*cos,stroke=d.lineWidthUnit*s;return{anchor:a,rect:[a[0]-rw/2-stroke/2,a[1]-rh/2-stroke/2,rw+stroke,rh+stroke],filter:samplingFilter(camera,level)}};
export const terrainRect=(chunk:RenderTerrainChunk,camera:RenderCamera):{anchor:RenderPoint;rect:RenderRect;filter:RenderFilter}=>{const s=screenScale(camera),a=s/chunk.level,c=cameraTranslation(camera),raw:[number,number]=[chunk.unitAnchor[0]*s+c[0]-chunk.nativeOffset[0]*a,chunk.unitAnchor[1]*s+c[1]-chunk.nativeOffset[1]*a],integer=Math.abs(a-Math.round(a))<=1e-6,p:RenderPoint=integer?[Math.round(raw[0]),Math.round(raw[1])]:raw;return{anchor:p,rect:[p[0],p[1],chunk.nativeSize[0]*a,chunk.nativeSize[1]*a],filter:samplingFilter(camera,chunk.level)}};

/**
 * Runtime clip (the one sanctioned sprite split): a convex polygon in unit-screen offsets from the sprite's own
 * unit anchor, placed with the same device anchor as the sprite. A device pixel is kept exactly when its centre lies
 * inside every edge half-plane (inclusive within CLIP_EPSILON), in both adapters.
 */
export const CLIP_EPSILON = 1e-4;
export const clipPolygon = (sprite: RenderSprite, camera: RenderCamera): RenderPoint[] | null => {
  if (!sprite.clipPath?.length) return null;
  const s = screenScale(camera), anchor = spriteGeometry(sprite, camera).anchor;
  return sprite.clipPath.map(([x, y]) => [anchor[0] + x * s, anchor[1] + y * s] as const);
};
/** Inward half-planes [nx, ny, d] of a convex polygon (either winding): inside ⇔ nx·x + ny·y + d ≥ −CLIP_EPSILON. */
export const clipHalfPlanes = (polygon: readonly RenderPoint[]): [number, number, number][] => {
  let area = 0;
  for (let i = 0; i < polygon.length; i += 1) { const a = polygon[i]!, b = polygon[(i + 1) % polygon.length]!; area += a[0] * b[1] - b[0] * a[1]; }
  const sign = area >= 0 ? 1 : -1;
  return polygon.map((a, i) => {
    const b = polygon[(i + 1) % polygon.length]!, nx = -(b[1] - a[1]) * sign, ny = (b[0] - a[0]) * sign, length = Math.hypot(nx, ny) || 1;
    return [nx / length, ny / length, -(nx * a[0] + ny * a[1]) / length];
  });
};
/** Rows of device pixels whose centres lie inside the polygon: [y, xFirst, xLast] (the Canvas clip path's staircase). */
/** Reference staircase of a device polygon (tests and diagnostics; the draw path uses forEachClipSpanAt). */
export const clipSpans = (polygon: readonly RenderPoint[]): [number, number, number][] => {
  const planes = clipHalfPlanes(polygon), ys = polygon.map((p) => p[1]), xs = polygon.map((p) => p[0]), spans: [number, number, number][] = [];
  for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y += 1) {
    let lo = Math.floor(Math.min(...xs)) - 1, hi = Math.ceil(Math.max(...xs)) + 1;
    const cy = y + 0.5;
    for (const [nx, ny, d] of planes) {
      const rest = ny * cy + d + CLIP_EPSILON; // nx·(x+½) + rest ≥ 0
      if (Math.abs(nx) < 1e-12) { if (rest < 0) { lo = 1; hi = 0; } continue; }
      const bound = -rest / nx - 0.5;
      if (nx > 0) lo = Math.max(lo, Math.ceil(bound)); else hi = Math.min(hi, Math.floor(bound));
    }
    if (lo <= hi) spans.push([y, lo + 0, hi + 0]); // + 0 normalises −0
  }
  return spans;
};

/** Camera-independent clip data derived once per clip path array: unit-space half-planes and bounds. */
export interface UnitClip { readonly planes: Float64Array; readonly count: number; readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number }
const unitClips = new WeakMap<readonly RenderPoint[], UnitClip>();
let computedClips = 0;
export const clipCacheStats = (): { computed: number } => ({ computed: computedClips });
export function unitClip(path: readonly RenderPoint[]): UnitClip {
  let cached = unitClips.get(path);
  if (!cached) {
    const planes = clipHalfPlanes(path), flat = new Float64Array(planes.length * 3);
    planes.forEach(([nx, ny, d], i) => flat.set([nx, ny, d], i * 3));
    const xs = path.map((p) => p[0]), ys = path.map((p) => p[1]);
    cached = { planes: flat, count: planes.length, minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
    unitClips.set(path, cached); computedClips += 1;
  }
  return cached;
}
/**
 * Visits the device pixel rows kept by a clip, [y, first, last], without allocating: a device point p lies inside
 * when n·(p − anchor) + d·s ≥ −CLIP_EPSILON for every unit half-plane (the unit polygon scaled by s about the anchor).
 */
export function forEachClipSpanAt(clip: UnitClip, anchorX: number, anchorY: number, s: number, visit: (y: number, first: number, last: number) => void): void {
  const planes = clip.planes, y0 = Math.floor(anchorY + clip.minY * s), y1 = Math.ceil(anchorY + clip.maxY * s), x0 = Math.floor(anchorX + clip.minX * s) - 1, x1 = Math.ceil(anchorX + clip.maxX * s) + 1;
  for (let y = y0; y <= y1; y += 1) {
    let lo = x0, hi = x1;
    const cy = y + 0.5 - anchorY;
    for (let i = 0; i < clip.count; i += 1) {
      const nx = planes[i * 3]!, ny = planes[i * 3 + 1]!, rest = ny * cy + planes[i * 3 + 2]! * s + CLIP_EPSILON;
      if (Math.abs(nx) < 1e-12) { if (rest < 0) { lo = 1; hi = 0; } continue; }
      const bound = -rest / nx + anchorX - 0.5; // nx·(x + ½ − anchorX) + rest ≥ 0
      if (nx > 0) lo = Math.max(lo, Math.ceil(bound)); else hi = Math.min(hi, Math.floor(bound));
    }
    if (lo <= hi) visit(y, lo + 0, hi + 0);
  }
}
export const forEachClipSpan = (sprite: RenderSprite, camera: RenderCamera, visit: (y: number, first: number, last: number) => void): void => {
  if (!sprite.clipPath?.length) return;
  const anchor = spriteGeometry(sprite, camera).anchor;
  forEachClipSpanAt(unitClip(sprite.clipPath), anchor[0], anchor[1], screenScale(camera), visit);
};
/** Writes device half-planes (nx, ny, d, 0) into `out`; returns whether any value changed. */
export function deviceClipPlanesInto(clip: UnitClip, anchorX: number, anchorY: number, s: number, out: Float32Array): boolean {
  let changed = false;
  for (let i = 0; i < out.length / 4; i += 1) {
    const inside = i < clip.count, nx = inside ? clip.planes[i * 3]! : 0, ny = inside ? clip.planes[i * 3 + 1]! : 0, d = inside ? clip.planes[i * 3 + 2]! * s - nx * anchorX - ny * anchorY : 0;
    const a = Math.fround(nx), b = Math.fround(ny), c = Math.fround(d), j = i * 4;
    if (out[j] !== a || out[j + 1] !== b || out[j + 2] !== c || out[j + 3] !== 0) { out[j] = a; out[j + 1] = b; out[j + 2] = c; out[j + 3] = 0; changed = true; }
  }
  return changed;
}
